import React, { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { AuthToken, AuthUser } from './user';


export interface UserContextType {
    user: AuthUser | undefined,    
    logIn: (data: AuthToken) => void
    logOut: () => void
    isUserLoggedIn: () => boolean
}

export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUserContext must be used within a UserContextProvider');
    }
    return context;
};

const UserContext = createContext<UserContextType | undefined>(undefined);
export const UserContextProvider: React.FC<{ children?: React.ReactNode }>
    = ({ children }) => {
        const [user, setUser] = useState<AuthUser>();
        const [isInitialized, setIsInitialized] = useState(false); // To prevent updates during render

        //const [isLoggedIn, setIsLoggedIn] = useState(false);

        const isTokenExpired = (token: string): boolean => {
            try {
                const decoded: { exp: number } = jwtDecode(token);
                return decoded.exp * 1000 < Date.now(); // Convert exp to milliseconds
            } catch (e) {
                console.error("Invalid token", e);
                return true;
            }
        };

        const resetUserState = () => {
            setUser(undefined);           
        };

        const logIn = (data: AuthToken) => {
            localStorage.removeItem("auth");
            if (data.token) {
                if (isTokenExpired(data.token)) {
                    console.error("Token is expired");
                    return;
                }
                
                localStorage.setItem("auth", JSON.stringify(data));
                setUser({ id: String(data.Id), token: data.token, name: data.username });                
            }
        };

        const logOut = () => {
            localStorage.removeItem("auth");
            resetUserState();
        };

        const isUserLoggedIn = () => {
            if (!localStorage.getItem('auth')) {
                resetUserState();
                return false;
            }

            const storedToken = localStorage.getItem('auth');
            if(!storedToken){
                resetUserState();
                return false;
            }

            const authToken = JSON.parse(storedToken) as AuthToken;
            if (isTokenExpired(authToken.token)) {
                console.error("Stored token is expired");                
                localStorage.removeItem("auth");
                resetUserState();
                return false;
            }

            // try {
            //     const res = await client.get("/auth/check-token");
            //     const isValid = res.data as boolean;
            //     setIsLoggedIn(isValid);
            // } catch (e) {
            //     localStorage.removeItem("auth");
            //     resetUserState();
            // }

            return true;
        };

        // Initialize user state on mount
        useEffect(() => {
            if (isInitialized) return; // Prevent repeated initialization
            
            const storedToken = localStorage.getItem('auth');
            if (storedToken) {
                const authToken = JSON.parse(storedToken) as AuthToken;
                if (!isTokenExpired(authToken.token)) {
                    setUser({ id: String(authToken.Id), token: authToken.token, name: authToken.username });
                } else {
                    localStorage.removeItem('auth');
                }
            }
            setIsInitialized(true); // Mark initialization as complete
        }, [isInitialized]);

        const contextValue = {
            user,
            logIn,
            logOut,
            isUserLoggedIn
        };

        return (
            <UserContext.Provider value={contextValue}>
                {children}
            </UserContext.Provider>
        );
    };